import { BoxProps, Button, useTheme } from '@mui/material';
import { PropsWithChildren, ReactNode, useCallback } from 'react';
import { useNavigate } from 'react-router';

import { HeaderMobile } from '@/components/Header/HeaderMobile';

import { useAppTheme } from '@/context/AppThemeContext';

import { useAppDispatch, useAppSelector } from '@/hooks/redux';
import { useResponsive } from '@/hooks/useResponsive';
import { useTranslate } from '@/hooks/useTranslate';

import { signOut } from '@/redux/auth/actions';
import { selectLoggedInUser } from '@/redux/auth/selectors';

import { ColorExtractFn } from '@/types/components/common';
import { ProgressDotsProps } from '@/types/components/data-display';
import { HeaderMobileProps } from '@/types/components/layout';

import { getDataTestId } from '@/utils/browser';

import { BackgroundPaper } from './BackgroundPaper';
import { BackgroundPaperProps } from './Page.interface';
import { PendingApproval } from './PendingApproval';

import {
  PageBackground,
  PageContent,
  PageFormContainer,
  PageLogo,
  PageNavBar,
  StyledProgress,
} from './Page.styled';

export type PageProps = {
  hasDarkBackground?: boolean;
  paperProps?: BackgroundPaperProps;
  hasLogo?: boolean;
  progressProps?: ProgressDotsProps;
  hasBackToLoginButton?: boolean;
  backgroundChildren?: ReactNode;
  bgColor?: ColorExtractFn;
  showPendingApproval?: boolean;
  headerMobile?: HeaderMobileProps | boolean;
  formContainerProps?: BoxProps;
} & PropsWithChildren;

export function Page({
  hasDarkBackground,
  paperProps,
  hasLogo,
  progressProps,
  hasBackToLoginButton,
  children,
  backgroundChildren,
  bgColor,
  showPendingApproval,
  headerMobile,
  formContainerProps,
}: PageProps) {
  const user = useAppSelector(selectLoggedInUser);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const { t } = useTranslate();
  const { theme: appThemeConfig } = useAppTheme();
  const { isMobile } = useResponsive();

  const backgroundColor = bgColor?.(theme);

  const onBackToLogin = useCallback(() => {
    if (!user) {
      navigate(-1);
    } else {
      dispatch(signOut());
    }
  }, [dispatch, navigate, user]);

  return (
    <PageBackground isDark={hasDarkBackground} bgColor={backgroundColor}>
      {backgroundChildren}
      {isMobile ? null : <BackgroundPaper {...paperProps} />}
      {showPendingApproval && <PendingApproval />}
      {isMobile && headerMobile && (
        <HeaderMobile
          {...(typeof headerMobile !== 'boolean' ? headerMobile : {})}
        />
      )}
      <PageContent>
        <PageFormContainer {...formContainerProps}>
          {hasLogo && <PageLogo src={appThemeConfig.logo} />}
          {progressProps && !isMobile && (
            <StyledProgress isOnDark={hasDarkBackground} {...progressProps} />
          )}
          {children}
          {hasBackToLoginButton && (
            <PageNavBar isDarkBg={hasDarkBackground}>
              <Button
                {...getDataTestId(`page-action-back-to-login`)}
                onClick={onBackToLogin}
                variant="text"
              >
                {t('layout.page.button.backToLogin')}
              </Button>
            </PageNavBar>
          )}
        </PageFormContainer>
      </PageContent>
    </PageBackground>
  );
}
